/**
 * Search Hook
 * Provides search functionality with state management
 */

import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface UseSearchOptions {
  defaultQuery?: string;
  defaultFilters?: Record<string, any>;
  syncWithUrl?: boolean;
  paramName?: string;
}

export interface SearchState {
  query: string;
  filters: Record<string, any>;
  isSearching: boolean;
}

export function useSearch(options: UseSearchOptions = {}) {
  const {
    defaultQuery = '',
    defaultFilters = {},
    syncWithUrl = true,
    paramName = 'q'
  } = options;

  const location = useLocation();
  const navigate = useNavigate();

  // Initialize state from URL if syncWithUrl is enabled
  const getInitialState = (): SearchState => {
    if (syncWithUrl) {
      const params = new URLSearchParams(location.search);
      const urlQuery = params.get(paramName) || defaultQuery;
      
      // Parse filters from URL
      const urlFilters: Record<string, any> = { ...defaultFilters };
      params.forEach((value, key) => {
        if (key !== paramName && key !== 'limit' && key !== 'offset') {
          urlFilters[key] = value;
        }
      });

      return {
        query: urlQuery,
        filters: urlFilters,
        isSearching: false
      };
    }

    return {
      query: defaultQuery,
      filters: defaultFilters,
      isSearching: false
    };
  };

  const [searchState, setSearchState] = useState<SearchState>(getInitialState);

  // Update URL when search state changes
  useEffect(() => {
    if (!syncWithUrl) return;

    const params = new URLSearchParams(location.search);
    
    // Update query parameter
    if (searchState.query) {
      params.set(paramName, searchState.query);
    } else {
      params.delete(paramName);
    }

    // Update filter parameters
    Object.keys(searchState.filters).forEach(key => {
      if (searchState.filters[key]) {
        params.set(key, searchState.filters[key]);
      } else {
        params.delete(key);
      }
    });

    // Navigate to new URL without adding to history
    const newSearch = params.toString();
    if (location.search !== `?${newSearch}`) {
      navigate({ search: newSearch }, { replace: true });
    }
  }, [searchState, location.search, navigate, syncWithUrl, paramName]);

  // Set search query
  const setQuery = useCallback((query: string) => {
    setSearchState(prev => ({ ...prev, query }));
  }, []);

  // Set filters
  const setFilters = useCallback((filters: Record<string, any>) => {
    setSearchState(prev => ({ ...prev, filters }));
  }, []);

  // Set both query and filters
  const setSearch = useCallback((query: string, filters?: Record<string, any>) => {
    setSearchState(prev => ({
      ...prev,
      query,
      filters: filters || prev.filters
    }));
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchState({
      query: '',
      filters: {},
      isSearching: false
    });
  }, []);

  // Set searching state
  const setIsSearching = useCallback((isSearching: boolean) => {
    setSearchState(prev => ({ ...prev, isSearching }));
  }, []);

  // Get search parameters for API calls
  const getSearchParams = useCallback(() => {
    const params: Record<string, any> = {};
    
    if (searchState.query) {
      params[paramName] = searchState.query;
    }
    
    Object.keys(searchState.filters).forEach(key => {
      if (searchState.filters[key] !== undefined && searchState.filters[key] !== '') {
        params[key] = searchState.filters[key];
      }
    });
    
    return params;
  }, [searchState, paramName]);

  return {
    query: searchState.query,
    filters: searchState.filters,
    isSearching: searchState.isSearching,
    setQuery,
    setFilters,
    setSearch,
    clearSearch,
    setIsSearching,
    getSearchParams
  };
}

export default useSearch;